/**
 * AddEditBackgroundMusicV2 — V2 port of the Background Music edit
 * form. Same Redux fetch / submit graph as v1
 * (Pages/BackgroundMusicPage/AddEditBackgroundMusic.js) so the API
 * contract is byte-identical.
 *
 *   • fetchOneBGMWithRelatedContent → returns {bgm fields, sessions,
 *     quick_taps, total_usage}
 *   • addEditBGM({ bgm_json }, navigate)
 *   • deleteBGM(id, navigate)
 *
 * Sections:
 *   01 Basic info       (title required)
 *   02 Audio            (AudioTile — picks up duration via
 *                        onLoadedMetadata, same as the
 *                        Sessions / Challenges intro flows)
 *   03 Usage (edit-only) — collapsible lists of Sessions +
 *                          Quick Taps that reference this track,
 *                          each row with a lock-state badge so
 *                          ops can spot pinned-music rows.
 *   04 Danger zone      (edit-only)
 *
 * No right-rail preview — background music doesn't have a
 * library-tile representation.
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  shouldConfirmReplacement,
  replacementWarningHtml,
} from '../../../../utils/backgroundMusicReplacement';

import LoadingBackdrop from '../../../Common/LoadingBackdrop';
import {
  addEditBGM,
  fetchOneBGMWithRelatedContent,
  deleteBGM,
} from '../../../../actions/backgroundMusicActions';
import { uploadAudio, clearMedia } from '../../../../actions/mediaActions';
import { preventEnterFormSubmission } from '../../../../utils/formUtils';

import { Button } from '../../../redesign';
import {
  FormSection,
  FieldBlock,
  FieldRow,
  TextInput,
  AudioTile,
} from '../../../redesign/edit';

/* ─── Inline icon set (kept local — these are usage-specific) ── */

const LockClosed = (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const LockOpen = (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 9.9-1" />
  </svg>
);

/* ─── Single usage row (session OR quick tap) ─────────────────── */

function UsageRow({ item, kind }) {
  const isLocked = item.is_music_locked === 1;
  return (
    <div
      className="flex items-center"
      style={{
        gap: 10,
        padding: '8px 12px',
        background: '#FAF7F1',
        border: '1px solid #E8E0CF',
        borderRadius: 8,
        marginBottom: 6,
        minWidth: 0,
      }}
    >
      <span
        className="font-condensed font-bold uppercase"
        style={{
          fontSize: 9.5,
          letterSpacing: '0.08em',
          padding: '2px 7px',
          borderRadius: 999,
          background: kind === 'session' ? '#75A29333' : '#E58A6A33',
          color: kind === 'session' ? '#5C7E72' : '#C4715B',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {kind === 'session' ? 'Session' : 'Quick tap'}
      </span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          className="text-navy"
          style={{
            fontSize: 13,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={item.title}
        >
          {item.title}
        </div>
        {(item.parent_type || item.parent_name) && (
          <div
            className="text-text-muted"
            style={{
              marginTop: 1,
              fontSize: 10.5,
              letterSpacing: '0.02em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.parent_type ? (
              <span style={{ textTransform: 'uppercase', fontWeight: 600 }}>
                {item.parent_type}
              </span>
            ) : null}
            {item.parent_type && item.parent_name ? ' · ' : ''}
            {item.parent_name || ''}
          </div>
        )}
      </div>

      <span
        className="inline-flex items-center"
        style={{
          gap: 4,
          padding: '2px 7px',
          borderRadius: 999,
          background: isLocked ? '#75A29333' : '#9CA1A81F',
          color: isLocked ? '#5C7E72' : '#9CA1A8',
          fontSize: 10,
          fontFamily: 'var(--font-condensed)',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          flexShrink: 0,
        }}
        title={
          isLocked
            ? 'Music is locked on this row — admins must explicitly unlock to swap it'
            : 'Music is unlocked — can be swapped freely'
        }
      >
        {isLocked ? LockClosed : LockOpen}
        {isLocked ? 'Locked' : 'Unlocked'}
      </span>
    </div>
  );
}

/* ─── Main ─────────────────────────────────────────────────────── */

const AddEditBackgroundMusicV2 = forwardRef(function AddEditBackgroundMusicV2(
  { isEdit, selectedId, onLoaded },
  ref,
) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [state, setState] = useState({
    title: '',
    audio_url: '',
    // The file this track had when the form loaded. Replacing it now changes
    // what every locked session plays, so the save path compares against it.
    original_audio_url: '',
    audio_preview: null,
    audio_length: 0,
    isSetToEdit: false,
  });

  const [relatedContent, setRelatedContent] = useState({
    sessions: [],
    quick_taps: [],
    total_usage: 0,
    loading: false,
  });

  const [isSaving, setIsSaving] = useState(false);

  // Synchronous re-entrancy lock. isSaving is React state set only after the
  // confirmation await, so it cannot gate the dialog window; without this a
  // second Save click while the dialog is open opens a second dialog and can
  // dispatch the save twice.
  const isSubmittingRef = useRef(false);

  const isFetching = useSelector((s) => s.background_musics?.isFetching);
  const audio = useSelector((s) => s.media?.audio);

  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  /* ─── Fetch on mount (1:1 with v1) ─────────────────────────── */
  useEffect(() => {
    if (!isEdit || !selectedId) return;
    setRelatedContent((prev) => ({ ...prev, loading: true }));
    (async () => {
      try {
        const bgmData = await dispatch(fetchOneBGMWithRelatedContent(selectedId));
        if (bgmData && bgmData.bgm_id) {
          setState({
            title: bgmData.bgm_title,
            audio_url: bgmData.bgm_url,
            original_audio_url: bgmData.bgm_url,
            audio_preview: bgmData.bgm_url,
            audio_length: bgmData.bgm_audio_length || 0,
            isSetToEdit: true,
          });
          setRelatedContent({
            sessions: bgmData.sessions || [],
            quick_taps: bgmData.quick_taps || [],
            total_usage: bgmData.total_usage || 0,
            loading: false,
          });
          if (onLoadedRef.current) {
            onLoadedRef.current({
              bgm_title: bgmData.bgm_title,
              bgm_updated_at: bgmData.bgm_updated_at ?? bgmData.updated_at ?? null,
            });
          }
        }
      } catch (error) {
        console.error('Error fetching background music data:', error);
        setRelatedContent((prev) => ({ ...prev, loading: false }));
      }
    })();
  }, [dispatch, isEdit, selectedId]);

  /* Redux media.audio settle — same as v1. */
  useEffect(() => {
    if (audio) {
      setState((prev) => ({
        ...prev,
        audio_url: audio,
        audio_preview: audio,
      }));
    }
  }, [audio]);

  /* ─── Handlers ─────────────────────────────────────────────── */
  const handleAudioUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await dispatch(uploadAudio(file));
    } catch (error) {
      console.error('Error uploading audio:', error);
      Swal.fire('Error', 'Failed to upload audio file', 'error');
    }
  };

  const handleAudioDuration = (e) => {
    const duration = Math.floor(e.target.duration) || 0;
    setState((prev) => ({ ...prev, audio_length: Math.round(duration) }));
  };

  const handleRemoveAudio = () => {
    setState((prev) => ({
      ...prev,
      audio_url: '',
      audio_preview: null,
      audio_length: 0,
    }));
  };

  /* ─── Submit (1:1 with v1) ──────────────────────────────── */
  const submit = useCallback(async () => {
    if (isSubmittingRef.current) return;

    if (!state.title) {
      Swal.fire('Error', 'Please enter a title', 'error');
      return;
    }
    if (!state.audio_url) {
      Swal.fire('Error', 'Please upload an audio file', 'error');
      return;
    }
    if (!state.audio_length) {
      Swal.fire('Error', 'Please wait for the audio to load', 'error');
      return;
    }
    isSubmittingRef.current = true;

    if (
      shouldConfirmReplacement({
        isEdit,
        originalUrl: state.original_audio_url,
        nextUrl: state.audio_url,
        relatedContent,
      })
    ) {
      const confirmation = await Swal.fire({
        title: 'Replace this music file?',
        html: replacementWarningHtml(relatedContent),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, replace it',
        cancelButtonText: 'Cancel',
      });

      if (!confirmation.isConfirmed) {
        isSubmittingRef.current = false;
        return;
      }
    }

    setIsSaving(true);
    try {
      const payload = {
        bgm_title: state.title,
        bgm_url: state.audio_url,
        bgm_audio_length: state.audio_length,
        ...(isEdit && { bgm_id: selectedId }),
      };
      const result = dispatch(addEditBGM(
        { bgm_json: JSON.stringify(payload) },
        () => navigate('/background-musics'),
      ));
      dispatch(clearMedia());
      if (result && typeof result.finally === 'function') {
        result.finally(() => {
          isSubmittingRef.current = false;
          setIsSaving(false);
        });
      } else {
        setTimeout(() => {
          isSubmittingRef.current = false;
          setIsSaving(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Error saving background music:', error);
      Swal.fire('Error', 'Failed to save background music', 'error');
      isSubmittingRef.current = false;
      setIsSaving(false);
    }
  }, [state, isEdit, selectedId, dispatch, navigate, relatedContent]);

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    submit();
  };

  useImperativeHandle(ref, () => ({ submit }), [submit]);

  /* ─── Delete (1:1 with v1) ───────────────────────────────── */
  const handleDelete = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteBGM(selectedId, () => navigate('/background-musics')));
      }
    });
  };

  if (isEdit && !state.isSetToEdit && !relatedContent.loading) {
    return <LoadingBackdrop open />;
  }

  return (
    <>
      <LoadingBackdrop open={isSaving || (isFetching && !state.isSetToEdit)} />
      <form
        id="bgm-form"
        onSubmit={handleSubmit}
        onKeyDown={preventEnterFormSubmission}
        autoComplete="off"
        style={{ minWidth: 0, maxWidth: 920 }}
      >
        {/* 01 Basic info */}
        <FormSection title="Basic info" num="01">
          <FieldRow cols={2} minColWidth={240}>
            <FieldBlock label="Title" required htmlFor="f-bgm-title">
              <TextInput
                id="f-bgm-title"
                name="title"
                value={state.title}
                onChange={(e) => setState((prev) => ({ ...prev, title: e.target.value }))}
                required
              />
            </FieldBlock>
            <FieldBlock label="Duration" hint="Auto-detected from the uploaded file">
              <TextInput
                name="audio_length"
                value={state.audio_length ? `${Math.floor(state.audio_length / 60)}:${String(state.audio_length % 60).padStart(2, '0')}` : ''}
                placeholder="—"
                readOnly
              />
            </FieldBlock>
          </FieldRow>
        </FormSection>

        {/* 02 Audio */}
        <FormSection title="Audio" num="02">
          <AudioTile
            required
            hint="MP3 / M4A · uploaded once, then served from CloudFront"
            value={state.audio_preview || state.audio_url}
            onChange={handleAudioUpload}
            onLoadedMetadata={handleAudioDuration}
            onRemove={handleRemoveAudio}
          />
        </FormSection>

        {/* 03 Usage */}
        {isEdit && (
          <FormSection
            title="Content using this track"
            num="03"
            right={
              <span
                className="font-mono text-text-muted bg-cream border border-sand"
                style={{
                  fontSize: 11,
                  padding: '2px 8px',
                  borderRadius: 999,
                  whiteSpace: 'nowrap',
                }}
              >
                {relatedContent.total_usage || 0} item{relatedContent.total_usage === 1 ? '' : 's'}
              </span>
            }
          >
            {relatedContent.loading ? (
              <div
                className="text-text-muted"
                style={{
                  padding: 16,
                  fontSize: 13,
                  textAlign: 'center',
                  background: '#FAF7F1',
                  border: '1px dashed #E8E0CF',
                  borderRadius: 8,
                }}
              >
                Loading related content…
              </div>
            ) : relatedContent.total_usage === 0 ? (
              <div
                className="text-text-muted"
                style={{
                  padding: 16,
                  fontSize: 13,
                  textAlign: 'center',
                  background: '#FAF7F1',
                  border: '1px dashed #E8E0CF',
                  borderRadius: 8,
                }}
              >
                Not used by any sessions or quick taps yet.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {relatedContent.sessions.length > 0 && (
                  <div>
                    <div
                      className="font-condensed font-bold uppercase text-text-muted"
                      style={{
                        fontSize: 10.5,
                        letterSpacing: '0.08em',
                        marginBottom: 8,
                      }}
                    >
                      Audio sessions · {relatedContent.sessions.length}
                    </div>
                    {relatedContent.sessions.map((session, i) => (
                      <UsageRow
                        key={session.id ?? `session-${i}`}
                        item={session}
                        kind="session"
                      />
                    ))}
                  </div>
                )}
                {relatedContent.quick_taps.length > 0 && (
                  <div>
                    <div
                      className="font-condensed font-bold uppercase text-text-muted"
                      style={{
                        fontSize: 10.5,
                        letterSpacing: '0.08em',
                        marginBottom: 8,
                      }}
                    >
                      Video quick taps · {relatedContent.quick_taps.length}
                    </div>
                    {relatedContent.quick_taps.map((qt, i) => (
                      <UsageRow
                        key={qt.id ?? `qt-${i}`}
                        item={qt}
                        kind="quick_tap"
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </FormSection>
        )}

        {/* 04 Danger zone */}
        {isEdit && (
          <FormSection title="Danger zone" num="04">
            <div className="flex items-center" style={{ gap: 12, flexWrap: 'wrap' }}>
              <Button variant="danger" onClick={handleDelete} type="button">
                Delete track
              </Button>
              <span className="text-text-muted" style={{ fontSize: 12 }}>
                Permanent — cannot be undone.
                {relatedContent.total_usage > 0 && (
                  <>
                    {' '}
                    <RouterLink
                      to="/background-musics"
                      style={{ color: '#C4715B', textDecoration: 'underline' }}
                    >
                      Still attached to {relatedContent.total_usage} item
                      {relatedContent.total_usage === 1 ? '' : 's'}.
                    </RouterLink>
                  </>
                )}
              </span>
            </div>
          </FormSection>
        )}
      </form>
    </>
  );
});

export default AddEditBackgroundMusicV2;
